/* eslint-disable no-undef */
import React, { Component, Fragment } from "react";
import axios from "axios";
import { List } from "react-content-loader";
import {
  locationCode,
  moveInCostObject,
  unitObject,
  contactObject,
  amountDue,
  getInsuranceType,
  formatCurrency,
  get24HourFee,
  locationObject,
  reservationObject,
  getAuthToken,
} from "../../../Utils/Common";
import { logApiFail, logOpenEdgeRequest, logOpenEdgeResponse } from "../../../Utils/rollbar";
import { 
  getOrCreateOrderId, 
  createTransactionHistory, 
  updateTransactionStatus, 
  TRANSACTION_STATUS 
} from "../../../Utils/transactionStatus";
import withRetryPaymentCount from "../../HOC/withRetryPaymentCount";
import GoogleRecaptcha from "../../../shared/GoogleRecaptcha";
import CloudflareTurnstile from "../../../shared/CloudflareTurnstile";
import { useTurnstile } from "../../../Utils/captchaConfig";

class ProcessPaymentCard extends Component {
  constructor(props) {
    super(props);

    this.state = {
      url: "",
      loading: false,
      captchaToken: null,
      captchaVerified: false,
      showCaptcha: true,
      captchaError: null,
    };
  }

  async componentDidMount() {
    // Don't load payment gateway until captcha is verified
    if (typeof KioApp !== "undefined") {
      KioApp.StartKeyboard("", "", "number", false, "", "");
    }

    const listener = window.addEventListener("blur", () => {
      if (document.activeElement === document.querySelector("iframe")) {
        if (typeof KioApp !== "undefined") {
          KioApp.StartKeyboard("", "", "number", false, "", "");
        }
      }
      window.removeEventListener("blur", listener);
    });
  }

  onCaptchaVerify = (token) => {
    this.setState({
      captchaToken: token,
      captchaVerified: true,
      captchaError: null,
      showCaptcha: false,
    });
    this.loadPaymentGateway();
  };

  onCaptchaError = (err) => {
    console.error("reCAPTCHA error:", err);
    this.setState({
      captchaError: "Captcha verification failed. Please try again.",
      captchaVerified: false,
    });
  };

  loadPaymentGateway = async () => {
    this.setState({ loading: true });

    let totalAmount = amountDue();
    const contactInfo = contactObject();

    // Generate or get order ID for transaction tracking
    const orderId = getOrCreateOrderId('new_tenant_order_id');
    const reservationDetails = reservationObject();

    const data = {
      location_code: locationCode(),
      total_amount: parseFloat(totalAmount).toFixed(2),
      rent_amount: parseFloat(totalAmount).toFixed(2),
      return_url: window.location.origin + "/confirm-payment",
      contact: contactInfo,
      captcha_token: this.state.captchaToken, // Include captcha token
    };

    // Create transaction history record
    await createTransactionHistory({
      order_id: orderId,
      location_code: locationCode(),
      tenant_id: reservationDetails?.tenantId || null,
      amount: parseFloat(totalAmount).toFixed(2),
      payment_method: 'CC',
      payment_flow: 'new_tenant_hosted',
    });

    // Update status to PENDING (iframe loaded, waiting for user)
    await updateTransactionStatus(orderId, TRANSACTION_STATUS.PENDING, {
      step: 'iframe_loaded',
      paypage_url_requested: true,
    });

    logOpenEdgeRequest("/api/paygateway/hostpay/transactions", data);

    axios
      .post("/api/paygateway/hostpay/transactions", JSON.stringify(data), {
        headers: { "Content-Type": "application/json" },
      })
      .then(async (response) => {
        logOpenEdgeResponse("/api/paygateway/hostpay/transactions", response.data, true);
        this.setState({
          url: response.data.paypageurl,
          loading: false,
        });
      })
      .catch(async (e) => {
        logOpenEdgeResponse("/api/paygateway/hostpay/transactions", e.response?.data || e.message, false);
        logApiFail("/paygateway/hostpay/transactions", data, e);
        
        // Update status to FAILED
        await updateTransactionStatus(orderId, TRANSACTION_STATUS.FAILED, {
          step: 'openedge_iframe_request',
          error: e.response?.data || e.message,
        });

        // Extract error message from API response
        let errorMessage = "Payment gateway error. Please try again.";
        if (e.response && e.response.data) {
          if (e.response.data.error) {
            errorMessage = e.response.data.error;
          } else if (e.response.data.message) {
            errorMessage = e.response.data.message;
          }
        } else if (e.message) {
          errorMessage = e.message;
        }

        this.setState({
          loading: false,
          captchaError: errorMessage,
          showCaptcha: true,
          captchaVerified: false,
        });
      });
  };

  componentWillUnmount() {
    if (typeof KioApp !== "undefined") {
      KioApp.CloseKeyboard();
    }
  }

  render() {
    const moveInCost = moveInCostObject();
    const unit = unitObject();
    const feeFor24Hours = get24HourFee()

    return (
      <Fragment>
        <link rel="stylesheet" href="/css/captcha.css" />
        <div className="wrapper">
          <div className="container">
            <div className="row">
              <div className="col-sm-12 col-md-8">
                {this.state.showCaptcha ? (
                  <div className="captcha-container">
                    <div className="captcha-header">
                      <h4 className="captcha-title">Security Verification</h4>
                      <p className="captcha-description">
                        Please complete the security verification to proceed with your payment.
                      </p>
                    </div>

                    {useTurnstile ? (
                      <CloudflareTurnstile
                        onVerify={this.onCaptchaVerify}
                        onError={this.onCaptchaError}
                        onExpire={this.onCaptchaError}
                        theme="auto"
                        size="normal"
                      />
                    ) : (
                      <GoogleRecaptcha
                        onVerify={this.onCaptchaVerify}
                        onError={this.onCaptchaError}
                      />
                    )}

                    {this.state.captchaError && (
                      <div className="alert alert-danger mt-3" role="alert">
                        {this.state.captchaError}
                      </div>
                    )}

                    <p className="text-muted small mt-3 mb-0">
                      This security check helps protect against automated attacks and ensures a secure payment process.
                    </p>
                  </div>

                ) : this.state.loading ? (
                  <List backgroundColor={"#ccc"} />
                ) : (
                  <div>
                    <iframe
                      title="Paygateway Form"
                      className="paygateway-form"
                      src={this.state.url}
                      width="100%"
                    ></iframe>
                    <div className="credit-number-mask" />
                  </div>
                )}
              </div>
              <div className="col-sm-12 col-md-4">
                <h5 className="text-grey">Unit Information</h5>
                <div className="p-3 border rounded">
                  <p>
                    Your monthly rate for this unit is{" "}
                    {formatCurrency(moveInCost.RentDetails.TotalRentAmount)}
                  </p>
                  {moveInCost.InsuranceDetails.InsuranceAmount <=
                    0 ? null : (
                    <p>
                      <b>{getInsuranceType()} Amount:</b>{" "}
                      {formatCurrency(
                        moveInCost.InsuranceDetails.InsuranceAmount
                      )}
                    </p>
                  )}
                  {moveInCost.FeeDetails.FeesAmount <= 0 ? null : (
                    <p>
                      <b>Fee Amount:</b>{" "}
                      {formatCurrency(moveInCost.FeeDetails.FeesAmount)}
                    </p>
                  )}
                  {feeFor24Hours && (
                    <p>
                      <b>24-Hour Upgrade:</b> {formatCurrency(feeFor24Hours.amount)}
                    </p>
                  )}
                  {moveInCost.RentDetails.TaxOnRent <= 0 ? null : (
                    <p>
                      <b>Tax Amount:</b>{" "}
                      {formatCurrency(moveInCost.RentDetails.TaxOnRent)}
                    </p>
                  )}
                  {moveInCost.DiscountDetails.DiscountValue <= 0 ? null : (
                    <p>
                      <b>Discount: </b>
                      {formatCurrency(moveInCost.DiscountDetails.DiscountValue)}
                    </p>
                  )}
                  <p>
                    <b>Unit Type:</b> {unit.length} x {unit.breadth} {unit.sizeString}{" "}
                    {unit.unitTypeDetailedDescription}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div >
      </Fragment >
    );
  }
}

const PAGE_ID = "new_tenant_checkout_process_payment_card_retry_count";
const PAYMENT_METHOD_RETURN_URL = "/lease-payment";
export default withRetryPaymentCount(ProcessPaymentCard, PAGE_ID, PAYMENT_METHOD_RETURN_URL);
